"use client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface DatePickerProps {
  date?: Date;
  onDateChange?: (date?: Date | string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  closeOnSelect?: boolean;
  asString?: boolean;
  disablePastDates?: boolean;
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Selecione uma data",
  disabled = false,
  className,
  closeOnSelect = false,
  asString = false,
  disablePastDates = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  function handleDateSelect(selectedDate: Date | undefined) {
    if (onDateChange) {
      onDateChange(asString ? selectedDate?.toISOString() : selectedDate);
      if (closeOnSelect) {
        setOpen(false);
      }
    }
  }

  // Get today's date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            format(date, "PPP", { locale: ptBR })
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          locale={ptBR}
          disabled={disablePastDates ? { before: today } : undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
